import { BUILDER_ROW_TYPES } from '../../../../../constants/campaignBuilder.js';
import type { BudgetRows } from '../../../../../types/predictivePlanning.js';
import { BuilderTableRow } from '../../../types.js';

export const getPlatformIdentifier = (
  row: BuilderTableRow,
): string | undefined => {
  const { type, nameKey, parentInfo } = row.original;
  if (
    type === BUILDER_ROW_TYPES.PLATFORM ||
    type === BUILDER_ROW_TYPES.MEDIA_NETWORK
  ) {
    return nameKey;
  }
  if (type === BUILDER_ROW_TYPES.TILE) {
    return parentInfo?.name;
  }
  return undefined;
};

const checkLock = (mediaRows: BudgetRows, id: string): boolean => {
  const rows = Object.keys(mediaRows).reduce(
    (acc: any, rowId: any) =>
      mediaRows[rowId].parentId === id ? [...acc, mediaRows[rowId]] : acc,
    [],
  );

  const lockedRows = rows.filter((row: any) => row.isLocked);

  return lockedRows.length === rows.length;
};

const getSubRowsIds = (mediaRows: BudgetRows, id: string | number): string[] =>
  Object.keys(mediaRows).reduce(
    (acc: string[], rowId) =>
      Number(mediaRows[rowId].parentId) === Number(id)
        ? [...acc, rowId, ...getSubRowsIds(mediaRows, rowId)]
        : acc,
    [],
  );

const setTileStatus = (mediaRows: BudgetRows, rowId: string): BudgetRows => {
  const currentRow = mediaRows[rowId];
  const parentRow = mediaRows[currentRow.parentId || ''];

  if (parentRow.isLocked === currentRow.isLocked) {
    return mediaRows;
  }

  const isLocked = checkLock(mediaRows, currentRow.parentId || '');

  if (isLocked === parentRow.isLocked) {
    return mediaRows;
  }

  const rows = {
    ...mediaRows,
    [currentRow.parentId || '']: {
      ...parentRow,
      isLocked,
    },
  };

  return parentRow.type === BUILDER_ROW_TYPES.CHANNEL
    ? rows
    : setTileStatus(rows, currentRow.parentId || '');
};

const setChannelStatus = (mediaRows: BudgetRows, rowId: string): BudgetRows => {
  const ids = getSubRowsIds(mediaRows, rowId);

  return Object.keys(mediaRows).reduce(
    (acc, id) =>
      ids.includes(id)
        ? {
            ...acc,
            [id]: { ...mediaRows[id], isLocked: mediaRows[rowId].isLocked },
          }
        : acc,
    mediaRows,
  );
};

const setPlatformStatus = (mediaRows: any, rowId: any) => {
  const rows = setChannelStatus(mediaRows, rowId);
  return setTileStatus(rows, rowId);
};

export const getNewMediaRows = (
  rows: BudgetRows,
  currentRow: BuilderTableRow,
): BudgetRows => {
  const mediaRows = {
    ...rows,
    [currentRow.id]: {
      ...rows[currentRow.id],
      isLocked: !rows[currentRow.id]?.isLocked,
    },
  };

  switch (currentRow.original.type) {
    case BUILDER_ROW_TYPES.TILE:
      return setTileStatus(mediaRows, currentRow.id.toString());

    case BUILDER_ROW_TYPES.CHANNEL:
      return setChannelStatus(mediaRows, currentRow.id.toString());

    case BUILDER_ROW_TYPES.MEDIA_NETWORK:
    case BUILDER_ROW_TYPES.PLATFORM:
      return setPlatformStatus(mediaRows, currentRow.id);

    default:
      return mediaRows;
  }
};
